import { useEffect, useMemo, useState } from "react";
import { Download, FileText, BadgeCheck, Receipt, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { Card, PageHeader, EmptyState, formatDate, formatDateTime } from "../ui";
import { useAuth } from "../AuthContext";

const TAGS = ["Todos", "Boleto", "Comunicado", "Contrato", "Estatuto", "Outro"];

// Blob responses wrap the JSON error detail; unwrap it to show the real reason.
const extractBlobError = async (err, fallback) => {
  try {
    if (err?.response?.data instanceof Blob) {
      const parsed = JSON.parse(await err.response.data.text());
      if (parsed?.detail) return String(parsed.detail);
    } else if (err?.response?.data?.detail) {
      return String(err.response.data.detail);
    }
  } catch (parseErr) {
    console.warn("Falha ao ler detalhe do erro de download:", parseErr);
  }
  return fallback;
};

const DOC_LABELS = {
  documento_identificacao: "Documento de identificação",
  comprovante_endereco: "Comprovante de endereço",
  contrato_social: "Contrato Social",
  documento_representante: "Documento do representante legal",
};

const formatBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

export default function AssociateFiles() {
  const { user } = useAuth();
  const isAssociate = user?.role === "associado";
  const [files, setFiles] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [onboardingDocs, setOnboardingDocs] = useState({});
  const [filter, setFilter] = useState("Todos");

  useEffect(() => {
    api
      .get("/portal/files")
      .then((r) => setFiles(r.data))
      .catch(() => toast.error("Não foi possível carregar seus arquivos."));
    if (isAssociate) {
      api
        .get("/portal/onboarding/status")
        .then((r) => setOnboardingDocs(r.data?.docs || {}))
        .catch(() => {});
      api
        .get("/portal/receipts")
        .then((r) => setReceipts(r.data || []))
        .catch(() => {});
    }
  }, [isAssociate]);

  const otherFiles = useMemo(
    () => (files || []).filter((f) => f.kind !== "recibo"),
    [files],
  );
  const filtered = useMemo(() => {
    return filter === "Todos"
      ? otherFiles
      : otherFiles.filter((f) => f.tag === filter);
  }, [otherFiles, filter]);

  const downloadFile = async (file) => {
    if (file.available === false) {
      toast.error(
        "Arquivo físico não está mais disponível no servidor. Peça ao administrador para reenviar o documento."
      );
      return;
    }
    try {
      const res = await api.get(`/portal/files/${file.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name || "arquivo";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(await extractBlobError(err, "Erro ao baixar o arquivo."));
    }
  };

  const downloadOnboardingDoc = async (docType, entry) => {
    try {
      const res = await api.get(`/portal/onboarding/${docType}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = entry.file_name || `${docType}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(await extractBlobError(err, "Erro ao baixar o documento."));
    }
  };

  const downloadReceipt = async (r) => {
    try {
      const res = await api.get(`/portal/receipts/${r.id}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Recibo-${r.number.replace("/", "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(await extractBlobError(err, "Erro ao baixar o recibo."));
    }
  };

  const onboardingEntries = Object.entries(onboardingDocs || {}).filter(
    ([, v]) => v && v.file_name
  );

  return (
    <div data-testid="associate-files">
      <PageHeader
        title={isAssociate ? "Meus Arquivos" : "Arquivos"}
        subtitle={
          isAssociate
            ? "Boletos, comunicados, contratos, documentos do cadastro e demais arquivos pessoais."
            : "Arquivos enviados pela administração da ABLEMP a todos os membros."
        }
      />

      {onboardingEntries.length > 0 && (
        <Card className="p-5 mb-6" testid="onboarding-docs-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-700">
                <BadgeCheck size={12} /> Documentos do cadastro
              </div>
              <h3 className="mt-1.5 font-serif-display text-xl">
                Documentos enviados por você
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Documentos enviados no cadastro inicial. Disponíveis apenas para
                você e para a administração da ABLEMP.
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {onboardingEntries.length}{" "}
              {onboardingEntries.length === 1 ? "documento" : "documentos"}
            </span>
          </div>
          <ul className="divide-y divide-slate-200 border-t border-slate-200">
            {onboardingEntries.map(([key, doc]) => (
              <li
                key={key}
                data-testid={`onboarding-doc-${key}`}
                className="flex items-center justify-between gap-4 py-3"
              >
                <div className="flex items-start gap-4 min-w-0">
                  <div className="h-10 w-10 grid place-items-center rounded-sm bg-emerald-50 text-emerald-700 shrink-0">
                    <FileText size={18} strokeWidth={1.4} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {DOC_LABELS[key] || key}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap items-center gap-2">
                      <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-sm text-[10px] uppercase tracking-wide">
                        Cadastro
                      </span>
                      <span>{formatDateTime(doc.uploaded_at)}</span>
                      <span>•</span>
                      <span className="truncate max-w-[260px]">
                        {doc.file_name}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  data-testid={`download-onboarding-${key}`}
                  onClick={() => downloadOnboardingDoc(key, doc)}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
                >
                  <Download size={14} />
                  Baixar
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {isAssociate && receipts.length > 0 && (
        <Card className="p-5 mb-6" testid="receipts-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#002776]">
                <Receipt size={12} /> Recibos
              </div>
              <h3 className="mt-1.5 font-serif-display text-xl">
                Recibos emitidos para você
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Recibos numerados emitidos pela administração da ABLEMP. Caso
                algum recibo seja cancelado, ele permanece visível com a marcação
                correspondente.
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {receipts.length} {receipts.length === 1 ? "recibo" : "recibos"}
            </span>
          </div>
          <ul className="divide-y divide-slate-200 border-t border-slate-200">
            {receipts.map((r) => (
              <li
                key={r.id}
                data-testid={`receipt-row-${r.id}`}
                className={`flex items-center justify-between gap-4 py-3 ${
                  r.status === "cancelado" ? "opacity-70" : ""
                }`}
              >
                <div className="flex items-start gap-4 min-w-0">
                  <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#002776]/8 text-[#002776] shrink-0">
                    <Receipt size={18} strokeWidth={1.4} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium">
                      Recibo nº {r.number} — {formatBRL(r.amount)}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap items-center gap-2">
                      {r.status === "cancelado" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-sm text-[10px] uppercase tracking-wide">
                          <XCircle size={11} /> Cancelado
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-sm text-[10px] uppercase tracking-wide">
                          Emitido
                        </span>
                      )}
                      <span>{formatDateTime(r.issued_at)}</span>
                      {r.issued_by_name && (
                        <>
                          <span>•</span>
                          <span>por {r.issued_by_name}</span>
                        </>
                      )}
                    </div>
                    {r.reason && (
                      <div className="mt-1 text-sm text-slate-700 line-clamp-2">
                        {r.reason}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`receipt-download-${r.id}`}
                  onClick={() => downloadReceipt(r)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
                >
                  <Download size={14} /> Baixar PDF
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-4">
        {TAGS.map((t) => (
          <button
            key={t}
            data-testid={`filter-${t.toLowerCase()}`}
            onClick={() => setFilter(t)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border rounded-sm transition-colors ${
              filter === t
                ? "bg-[#0A0F14] text-white border-[#0A0F14]"
                : "bg-white text-slate-700 border-slate-300 hover:border-[#002776]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {files === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Nenhum arquivo nesta categoria"
          hint="Quando o administrador enviar boletos, comunicados ou outros documentos para você, eles aparecerão aqui."
        />
      ) : (
        <Card>
          <ul className="divide-y divide-slate-200">
            {filtered.map((f) => (
              <li
                key={f.id}
                data-testid={`file-row-${f.id}`}
                className="flex items-center justify-between gap-4 p-4 hover:bg-slate-50"
              >
                <div className="flex items-start gap-4 min-w-0">
                  <div className="h-10 w-10 grid place-items-center rounded-sm bg-slate-100 text-slate-600 shrink-0">
                    <FileText size={18} strokeWidth={1.4} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{f.title}</div>
                    <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap items-center gap-2">
                      <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-[10px] uppercase tracking-wide">
                        {f.tag}
                      </span>
                      <span>•</span>
                      <span>{formatDate(f.uploaded_at)}</span>
                      <span>•</span>
                      <span>{f.file_name}</span>
                      {f.available === false && (
                        <span
                          data-testid={`file-unavailable-${f.id}`}
                          className="px-2 py-0.5 bg-rose-50 border border-rose-200 rounded-sm text-[10px] uppercase tracking-wide text-rose-700"
                          title="O arquivo físico não está mais no servidor. Peça ao administrador para reenviar."
                        >
                          Indisponível
                        </span>
                      )}
                    </div>
                    {f.description && (
                      <div className="mt-1 text-sm text-slate-600 line-clamp-2">
                        {f.description}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`download-${f.id}`}
                  onClick={() => downloadFile(f)}
                  disabled={f.available === false}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download size={14} />
                  Baixar
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
